import {Component, effect, inject, OnDestroy, signal} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {firstValueFrom, mergeMap, Subject} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {Router} from "@angular/router";
import {DatePipe, NgClass} from "@angular/common";
import {SearchCroComponent} from "../../../../../../shared/common/search-cro/search-cro.component";
import {
    SearchSalesExecutive
} from "../../../../../../shared/common/search-sales-owner/search-sales-executive.component";
import {SearchCurrencyComponent, SearchCustomerComponent} from "../../../../../../shared";
import {SearchDeliveryComponent} from "../../../../../../shared/common/search-delivery/search-delivery.component";
import {SalesOrderNewService} from "../../../services";
import {AuthService, DotLoadingServices} from "../../../../../../../core";
import {NotificationService} from "../../../../../../services/notification.service";
import {AddressDTO, CustomerDTO} from "../../../../../../interfaces";
import {CustomerService} from "../../../../../../services";
import {SalesOrderGetNewDTO, SalesOrderNewDTO} from "../../../interface/sales-order/sales-order-new.entity";
import {PositiveNumberOnlyDirective} from "../../../../Shared/Directives/Positive-number-only.directive";

@Component({
    selector: 'app-sales-order-create',
    imports: [
        SearchCroComponent,
        SearchSalesExecutive,
        SearchCustomerComponent,
        SearchDeliveryComponent,
        FormsModule,
        SearchCurrencyComponent,
        NgClass,
        DatePipe,
        PositiveNumberOnlyDirective,
    ],
    templateUrl: './sales-order-create.component.html',
    styleUrl: './sales-order-create.component.scss',
    standalone: true
})
export class SalesOrderCreateComponent implements OnDestroy {
    protected customerService = inject(CustomerService);
    protected authService = inject(AuthService);
    protected salesOrderService = inject(SalesOrderNewService);
    protected notification = inject(NotificationService);
    protected loading = inject(DotLoadingServices);
    protected router = inject(Router);

    protected currentPoType = signal<string>('BULK-PO');
    protected rawPoNumber = signal<string>('');
    protected isDropdownOpen = signal<boolean>(false);
    protected isPoNumberExists = signal<boolean>(false);
    protected originalPoNumber = signal<string>('');
    salesExecutiveStatus = signal<boolean>(false);
    protected today: Date = new Date();
    protected isValidatingPo = signal<boolean>(false);

    protected salesOrder = signal<SalesOrderNewDTO>({
        customerId: -1,
        merchandiser: '',
        poNumber: '',
        poDate: '',
        poReceivedDate: '',
        poValue: '',
        salesExecutiveId: -1,
        deliveryDate: '',
        invoiceAddressId: -1,
        deliveryAddressId: -1,
        croId: -1,
        currencyId: -1,
    });

    protected deliveryAddresses = signal<AddressDTO[]>([]);
    protected invoiceAddresses = signal<AddressDTO[]>([]);

    // Subject to handle PO number validation requests
    private poCheckSubject = new Subject<{ poNumber: string, customerId: number }>();

    constructor() {
        effect(() => {
            const customer = this.customerService.active();

            if (customer) {
                this.updateCustomerAddresses(customer);

                if (customer?.isOrderActive === 0) {
                    this.showCustomerBlockAlert();

                    if (this.isSalesOrderCreate) {
                        this.customerService.initial();
                        this.resetCustomerBasedFields();
                        return;
                    }
                }

                if (this.isSalesOrderCreate) {
                    this.updateSalesOrderWithCustomerData(customer);
                    this.setDefaultDeliveryAddress();
                }
            }
        });

        // Set up debounced PO check pipeline
        this.poCheckSubject.pipe(
            distinctUntilChanged((prev, curr) => prev.poNumber === curr.poNumber && prev.customerId === curr.customerId),
            mergeMap(({ poNumber, customerId }) => {
                this.isValidatingPo.set(true);
                return this.salesOrderService.isPoExist(poNumber, customerId);
            })
        ).subscribe({
            next: (res) => {
                this.isValidatingPo.set(false);
                const recordCount = res.data.record_count;
                const responseSalesOrderId = res.data.sales_order_id;
                const activeSalesOrder = this.salesOrderService.active();
                const isUpdateMode = !!activeSalesOrder;

                // In update mode, allow if record count is 1 and matches current sales order ID
                if (isUpdateMode && recordCount === 1 && responseSalesOrderId === activeSalesOrder?.salesOrderId) {
                    this.isPoNumberExists.set(false);
                    return;
                }

                // Fail if record count is greater than 1 or in create mode with any existing record
                const exists = recordCount > 0;
                this.isPoNumberExists.set(exists);
                if (exists) {
                    this.notification.set({
                        type: 'alert',
                        message: `PO number "${this.salesOrder().poNumber}" already has an existing Sales Order. Please verify.`,
                        timeout: 7000
                    });
                }
            },
            error: () => {
                this.isValidatingPo.set(false);
                this.isPoNumberExists.set(false);
            }
        });

        this.initializeActiveSalesOrder();
    }

    private updateSalesOrderWithCustomerData(customer: CustomerDTO): void {
        if (!this.isSalesOrderCreate) {
            return;
        }
        this.salesOrder.update((value) => ({
            ...value,
            customerId: customer.customerId ?? -1,
            currencyId: customer.currency?.currencyId ?? -1,
            salesExecutiveId: customer.salesExecutiveId ?? -1,
        }));
    }

    private updateCustomerAddresses(customer: CustomerDTO): void {
        this.deliveryAddresses.set(
            customer.addresses?.filter((item: AddressDTO) => item.useForDelivery) ?? []
        );
        this.invoiceAddresses.set(
            customer.addresses?.filter((item: AddressDTO) => item.useForInvoice) ?? []
        );
    }

    private setDefaultDeliveryAddress(): void {
        const defaultDeliveryAddress = this.deliveryAddresses().find(
            (item) => item.isDefaultDelivery
        );

        if (defaultDeliveryAddress) {
            this.salesOrder.update((value) => ({
                ...value,
                deliveryAddressId: defaultDeliveryAddress.addressId,
            }));
        }
    }

    private initializeActiveSalesOrder(): void {
        const activeSalesOrder = this.salesOrderService.active();
        if (activeSalesOrder) {
            this.populateDataForUpdate(activeSalesOrder);

            const customer = activeSalesOrder.customer;
            if ((customer?.isActive ?? 0) === 0 || (customer?.isOrderActive ?? 0) === 0) {
                this.showCustomerBlockAlert();
            }
        }
    }

    protected onPoNumberBlur(): void {
        const formattedPoNumber = this.salesOrder().poNumber;
        this.checkPoExists(formattedPoNumber);
    }

    get isSalesOrderCreate(): boolean {
        return !this.salesOrderService.active();
    }

    protected onFieldSelected(field: keyof SalesOrderNewDTO, value: any) {
        let finalValue = value;

        if (field === 'croId' && typeof value === 'object' && value?.employeeId) {
            finalValue = value.employeeId;
        }

        this.salesOrder.update(current => ({ ...current, [field]: finalValue }));
        if (field === 'customerId') {
            this.checkPoExists(this.salesOrder().poNumber);
        }

        if (field === 'customerId') {
            if (finalValue === -1) {
                this.resetCustomerBasedFields();
                this.customerService.initial();
            }
            this.checkPoExists(this.salesOrder().poNumber);
        }
    }

    protected setPoType(poType: string) {
        this.currentPoType.set(poType);
        const currentRawNumber = this.rawPoNumber();
        const formattedPoNumber = this.getFormattedPoNumber(currentRawNumber, poType);
        this.salesOrder.update(value => ({ ...value, poNumber: formattedPoNumber }));
        this.toggleDropdown();
        this.checkPoExists(formattedPoNumber);
    }

    protected updateField(field: keyof SalesOrderNewDTO, value: any) {
        if (field === 'poNumber') {
            const trimmedValue = typeof value === 'string' ? value.trim().replace(/\s+/g, '') : value;
            const poType = this.currentPoType();
            const prefix = poType === 'FOC' ? 'FOC_' : poType === 'NO-PO' ? 'NO-PO_' : '';

            if (poType === 'BULK-PO') {
                this.rawPoNumber.set(trimmedValue);
            } else {
                if (!trimmedValue.startsWith(prefix)) {
                    this.salesOrder.update(current => ({ ...current, poNumber: prefix + this.rawPoNumber() }));
                    this.checkPoExists(prefix + this.rawPoNumber());
                    return;
                }
                const rawValue = trimmedValue.substring(prefix.length);
                this.rawPoNumber.set(rawValue);
            }

            const formattedPoNumber = this.getFormattedPoNumber(this.rawPoNumber(), poType);
            this.salesOrder.update(current => ({ ...current, poNumber: formattedPoNumber }));

            // CRITICAL: Trigger validation whenever PO number changes
            this.checkPoExists(formattedPoNumber);
        } else {
            this.salesOrder.update(current => ({ ...current, [field]: value }));
        }
    }

    protected toggleDropdown() {
        this.isDropdownOpen.update(value => !value);
    }

    private checkPoExists(poNumber: string) {
        if (!poNumber || this.salesOrder().customerId === -1) {
            this.isPoNumberExists.set(false);
            return;
        }

        const activeSalesOrder = this.salesOrderService.active();
        const isUpdateMode = !!activeSalesOrder;

        if (this.isOrderDelivered) {
            return;
        }

        this.poCheckSubject.next({ poNumber, customerId: this.salesOrder().customerId });
    }

    private async validatePoNumberBeforeSubmit(): Promise<boolean> {
        const poNumber = this.salesOrder().poNumber;
        const customerId = this.salesOrder().customerId;

        if (!poNumber || customerId === -1) {
            return true; 
        }
        
        if (this.isValidatingPo()) {
            this.notification.set({
                type: 'alert',
                message: 'Please wait while validating PO number.',
                timeout: 3000
            });
            return false; 
        }

        try {
            const res = await firstValueFrom(this.salesOrderService.isPoExist(poNumber, customerId));
            const recordCount = res.data.record_count;
            const responseSalesOrderId = res.data.sales_order_id;
            const activeSalesOrder = this.salesOrderService.active();
            const isUpdateMode = !!activeSalesOrder;

            // In update mode, allow if record count is 1 and matches current sales order ID
            if (isUpdateMode && recordCount === 1 && responseSalesOrderId === activeSalesOrder?.salesOrderId) {
                this.isPoNumberExists.set(false);
                return true;
            }

            // Fail if record count is greater than 1 or in create mode with any existing record
            const exists = recordCount > 0;
            this.isPoNumberExists.set(exists);

            if (exists) {
                this.notification.set({
                    type: 'alert',
                    message: `PO number "${poNumber}" already has an existing Sales Order. Please verify.`,
                    timeout: 7000
                });
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error validating PO number:', error);
            // In case of error, allow the operation to proceed
            return true;
        }
    }

    protected isValidCreation(): boolean {
        const s = this.salesOrder();
        const c = this.customerService.active();
        const a = this.salesOrderService.active();

        return (this.isPoNumberExists()) ||
            this.isValidatingPo() || // Add this check to prevent submission while validating
            !c ||
            (!this.isSalesOrderCreate && !a?.salesOrderId) ||
            [s.customerId, s.salesExecutiveId, s.poReceivedDate, s.deliveryAddressId, s.invoiceAddressId, s.currencyId].includes(-1) ||
            [s.poNumber, s.poDate, s.poValue, s.deliveryDate].some(v => typeof v === 'string' ? !v.trim() : !v);
    }

    protected async createSalesOrder(): Promise<void> {
        if (this.isValidCreation()) {
            this.showCustomerBlockAlert();
            return;
        }

        try {
            this.loading.set(true);

            // CRITICAL: Validate PO number one more time before proceeding
            const isPoValid = await this.validatePoNumberBeforeSubmit();
            if (!isPoValid) {
                this.loading.set(false);
                return;
            }

            const salesOrderData = { ...this.salesOrder() };
            salesOrderData.poValue = Number(salesOrderData.poValue);
            const activeSalesOrder = this.salesOrderService.active();
            const isUpdate = !!activeSalesOrder;

            let operation$;
            if (isUpdate) {
                const { createdBy, ...updateData } = salesOrderData as any;
                const payload = {
                    ...updateData,
                    updatedBy: this.authService.username()
                };
                operation$ = this.salesOrderService.update(activeSalesOrder!.salesOrderId, payload);
            } else {
                const { id, ...dataWithoutId } = salesOrderData as any;
                const payload = {
                    orders: [dataWithoutId],
                    createdBy: this.authService.username()
                };
                operation$ = this.salesOrderService.create(payload);
            }

            await firstValueFrom(operation$);

            this.salesOrderService.notifySalesOrderCreated();
            this.notification.set({
                type: 'success',
                message: `Sales order ${isUpdate ? 'updated' : 'created'} successfully.`,
            });

            if (!isUpdate) {
                this.router.navigate(['/sales-order']).then(success => {
                    this.cancel();
                });
            }

        } catch (error) {
            this.notification.set({
                type: 'error',
                message: `Failed to ${this.isSalesOrderCreate ? 'create' : 'update'} the sales order.`,
            });
            console.error(error);

        } finally {
            this.loading.set(false);
        }
    }

    private populateDataForUpdate(data: SalesOrderGetNewDTO): void {
        const poType = data.poNumber.startsWith('FOC_') ? 'FOC' : data.poNumber.startsWith('NO-PO_') ? 'NO-PO' : 'BULK-PO';
        const rawPoNumber = poType === 'BULK-PO' ? data.poNumber : data.poNumber.split('_')[1];

        this.currentPoType.set(poType);
        this.rawPoNumber.set(rawPoNumber);
        this.originalPoNumber.set(data.poNumber);

        this.salesOrder.set({
            customerId: data.customer.customerId,
            merchandiser: data.merchandiser,
            poNumber: data.poNumber,
            poDate: data.poDate,
            poReceivedDate: data.poReceivedDate,
            poValue: data.poValue,
            salesExecutiveId: data.salesPerson.employeeId,
            deliveryDate: data.deliveryDate,
            invoiceAddressId: data.invoiceAddress.addressId,
            deliveryAddressId: data.deliveryAddress.addressId,
            croId: data.customerService.employeeId,
            currencyId: data.currency.currencyId,
            createdBy: this.authService.username()
        });
    }

    get isOrderDelivered(): boolean {
        return (this.salesOrderService.active()?.totalDeliveredQuantity ?? 0) > 0;
    }

    protected enforcePrefix(event: Event) {
        const input = event.target as HTMLInputElement;
        const poType = this.currentPoType();
        const prefix = poType === 'FOC' ? 'FOC_' : poType === 'NO-PO' ? 'NO-PO_' : '';

        const trimmedValue = input.value.trim().replace(/\s+/g, '');

        if (poType !== 'BULK-PO' && !trimmedValue.startsWith(prefix)) {
            input.value = prefix + this.rawPoNumber();
        } else {
            input.value = trimmedValue;
        }

        // CRITICAL: Trigger validation whenever input changes
        // This ensures validation happens even when user types letters or any characters
        this.checkPoExists(input.value);
    }

    private getFormattedPoNumber(rawNumber: string, poType: string): string {
        switch (poType) {
            case 'NO-PO':
                return `NO-PO_${rawNumber}`;
            case 'FOC':
                return `FOC_${rawNumber}`;
            case 'BULK-PO':
            default:
                return rawNumber;
        }
    }

    protected showCustomerBlockAlert(): void {
        this.notification.set({
            type: 'alert',
            message: 'Customer is not allowed to create orders.',
            timeout: 6000,
        });
    }

    protected onDisabledButtonHover(): void {
        const messages: string[] = [];

        if (this.isValidCreation()) {
            messages.push('Please fill all required fields to continue.');
        }

        if (this.isPoNumberExists()) {
            messages.push('Invalid PO Number. Please verify.');
        }

        if (this.isValidatingPo()) {
            messages.push('Please wait while validating PO number.');
        }

        if (messages.length > 0) {
            this.notification.set({
                type: 'alert',
                message: messages.join(' '),
                timeout: 3000
            });
        }
    }

    private resetCustomerBasedFields(): void {
        if (!this.isSalesOrderCreate) {
            return;
        }
        this.salesOrder.update((value) => ({
            ...value,
            currencyId: -1,
            salesExecutiveId: -1,
            deliveryAddressId: -1,
            invoiceAddressId: -1,
        }));
        this.deliveryAddresses.set([]);
        this.invoiceAddresses.set([]);
    }

    protected cancel() {
        this.rawPoNumber.set('');
        this.originalPoNumber.set('');
        this.currentPoType.set('BULK-PO');
        this.isPoNumberExists.set(false);
        this.isValidatingPo.set(false);
        this.salesOrder.set({
            customerId: -1,
            merchandiser: '',
            poNumber: '',
            poDate: '',
            poReceivedDate: '',
            poValue: '',
            salesExecutiveId: -1,
            deliveryDate: '',
            invoiceAddressId: -1,
            deliveryAddressId: -1,
            croId: -1,
            currencyId: -1,
        });
        this.deliveryAddresses.set([]);
        this.invoiceAddresses.set([]);
        this.customerService.initial();
    }

    ngOnDestroy(): void {
        this.cancel();
        this.poCheckSubject.complete(); 
    }

    handleSalesOwnerStatus(status: boolean) {
        this.salesExecutiveStatus.set(status);
    }

    protected readonly Number = Number;
}